import schemify from 'url-schemify';

import { setTeamUrl } from '../actions/auth';

import { debugLog as log } from 'shared-services/logger-service';

import { DEFAULT_LOGIN_ROUTE } from 'constants/index';
import config from 'config/index';

import { switchTeam } from '../globals-service';
import { browserHistory } from './history';
import * as userStorageService from './user-storage-service';
import { getTeamUrl } from './store-service';
import { isDev, isElectron } from 'services/env-service';
import { getTeamUrlFromUrl } from '../utils/url';
import { hasSubteamAccess } from '../utils/subteam';

const cache = {};

const ALLOWED_REDIRECT = ['', '/', 'dashboard'];
const FORBIDDEN_REDIRECT_PATHS = ['', '/', 'dashboard', 'login', 'signup', 'signup/email', 'signup/verify', 'signup/profile', 'signup/team', 'signup/theme', 'signup/import', 'signup/invite', 'doc-mobile', 'auth-token/asana', 'auth-token/basecamp', 'auth-token/trello', 'accept_invite', 'reset_password', 'token', 'google-drive-share', 'external-auth', 'maintenance', 'external-redirect/zoom', 'external-redirect/webex', 'external-redirect/github', 'external-redirect/google', 'external-redirect/microsoft', 'microsoft/one-drive', 'auth-service/microsoft', 'electron-helper-browser'];
const FORBIDDEN_TEAM_REDIRECT_PATHS = ['login', 'signup', 'signup/email', 'signup/verify', 'signup/profile', 'signup/team', 'signup/theme', 'signup/import', 'signup/invite', 'doc-mobile', 'auth-token/asana', 'auth-token/basecamp', 'auth-token/trello', 'accept_invite', 'reset_password', 'google-drive-share', 'external-auth', 'external-redirect/zoom', 'external-redirect/webex', 'external-redirect/github', 'external-redirect/google', 'external-redirect/microsoft', 'microsoft/one-drive', 'auth-service/microsoft', 'electron-helper-browser'];

export const setRedirectPage = pathname => {
    cache.redirectPathname = pathname;
}

const LAST_SESSION_REDIRECT_ENABLED = false;

const getPathname = () => browserHistory.location.pathname.replace(/^\//, '');

export const redirectToTeam = teamUrl => {
    log('[REDIRECT TO TEAM] Start', teamUrl);

    if (isElectron()) {
        log('[REDIRECT TO TEAM] Halt - desktop app', teamUrl);
        return;
    }

    if (isDev()) {
        log('[REDIRECT TO TEAM] Halt - is dev', teamUrl);
        return;
    }

    if (!teamUrl) {
        log('[REDIRECT TO TEAM] Halt - no team', teamUrl);
        return;
    }

    if (FORBIDDEN_TEAM_REDIRECT_PATHS.includes(getPathname())) {
        log('[REDIRECT TO TEAM] Halt - forbidden path', getPathname());
        return;
    }

    if (getTeamUrl() !== teamUrl) {
        log('[REDIRECT TO TEAM] set cached team url:', teamUrl, 'Old:', getTeamUrl());
        setTeamUrl(teamUrl);
    }

    if (getTeamUrlFromUrl() === teamUrl) {
        log('[REDIRECT TO TEAM] url team is the same:', teamUrl, 'Url:', getTeamUrlFromUrl());
        return;
    }

    if (!window.location.host.includes(config.APP_DOMAIN)) {
        log('[REDIRECT TO TEAM] the domain is different, probably CNAME');
        return;
    }

    log('[REDIRECT TO TEAM] switch team:', teamUrl);
    switchTeam({
        teamUrl,
        keepPath: true
    });
}

export const redirectToCname = (cname, masterToken, teamToken, teamUrl) => {
    const schemifiedUrl = schemify(cname, { scheme: 'https' }).replace(/\/$/, '');

    if (schemify(window.location.host, { scheme: 'https' }) === schemifiedUrl) {
        return;
    }

    const path = window.location.href.split(window.location.host)[1] || '/';

    window.location = `${schemifiedUrl}/login-token/${masterToken}/${teamToken}?path=${encodeURIComponent(path)}&teamUrl=${encodeURIComponent(teamUrl)}`;
}

const isDesktopRoot = (pathname = getPathname()) => pathname.indexOf('/index.html') > 0;

export const redirectToPage = (subteams, projects, force) => {
    if (isDesktopRoot() && !force) {
        const lastSession = userStorageService.get('lastSession');
        log('[REDIRECT] Initial redirect for desktop to', (lastSession && !isDesktopRoot(lastSession.pathname) && lastSession.pathname) || DEFAULT_LOGIN_ROUTE);

        redirect((lastSession && !isDesktopRoot(lastSession.pathname) && lastSession.pathname) || DEFAULT_LOGIN_ROUTE);
        return;
    }

    if (!shouldRedirect(getPathname()) && !force) {
        log('[REDIRECT] Skip redirect for path:', getPathname());
        return;
    }

    if (cache.redirectPathname && cache.redirectPathname) {
        log('[REDIRECT] From redirect pathname');
        if (redirect(cache.redirectPathname, true)) {
            setRedirectPage(null);
            return;
        }
    }

    const lastSession = userStorageService.get('lastSession');

    if (LAST_SESSION_REDIRECT_ENABLED && lastSession && lastSession.timestamp < Date.now()) {
        const pathname = lastSession.pathname.replace(/^\//, '');

        if (getPathname() !== pathname && browserHistory.location.search !== '?no-redirect') {
            log('[REDIRECT] From session');
            userStorageService.set('lastSession', null);
            if (redirect(pathname)) {
                return;
            }
        }
    }

    redirectToProject(projects);
}

export const redirectToProject = (projects, subpage) => {
    const project = projects[0]

    if (project && project.id && project.id !== 'undefined') {
        log('[REDIRECT] to project');
        redirect('/' + project.id + (subpage ? `/${subpage}` : ''));
    }
}

export const redirectToSubteam = subteams => {
    const subteam = subteams.find(subteam => (subteam.is_general && hasSubteamAccess(subteam.id)) || hasSubteamAccess(subteam.id));

    if (subteam && subteam.id) {
        log('[REDIRECT] to subteam');
        redirect(`/portfolio/${subteam.id}`);
    }
}

const redirect = (path, skipCheck) => {
    if (skipCheck || canRedirect(path)) {
        log('[REDIRECT] To:', path);
        browserHistory.push(path);
        return true;
    }

    log('[REDIRECT] Not allowed:', path);
    return false;
}

const canRedirect = path => {
    return !FORBIDDEN_REDIRECT_PATHS.includes(path) && !['/', '//'].includes(path);
}

const shouldRedirect = path => {
    return ALLOWED_REDIRECT.includes(path);
}
